import * as yup from "yup";
import { type SpaceVisibility } from "@/react/types/Search/Space";
import { type SpaceType } from "@circle-react/types/Space";

export const queryParamsSchema = yup.object().shape({
  prefill_token: yup.string().optional(),
});

export type QueryParams = yup.InferType<typeof queryParamsSchema>;

export interface BaseSpacePrefillData {
  access?: SpaceVisibility;
  name?: string;
  space_group_id?: number;
  space_type?: LLMSpaceType;
}

export interface CourseSpacePrefillData extends BaseSpacePrefillData {
  course_type?: "self_paced" | "structured" | "scheduled";
}

export interface PostSpacePrefillData extends BaseSpacePrefillData {
  default_in_app_notification_setting?: boolean;
  default_mobile_notification_setting?: boolean;
  default_notification_setting?: boolean;
}

export interface EventSpacePrefillData extends BaseSpacePrefillData {
  auto_rsvp_invited_attendees?: boolean;
  default_in_app_notification_setting?: boolean;
  default_mobile_notification_setting?: boolean;
  default_notification_setting?: boolean;
}

export interface ChatSpacePrefillData extends BaseSpacePrefillData {
  notification_type?: "all" | "mentions" | "never";
}

export type MemberSpacePrefillData = BaseSpacePrefillData;

export interface ImageSpacePrefillData extends BaseSpacePrefillData {
  default_in_app_notification_setting?: boolean;
  default_mobile_notification_setting?: boolean;
  default_notification_setting?: boolean;
}

export type SpacePrefillData =
  | CourseSpacePrefillData
  | PostSpacePrefillData
  | EventSpacePrefillData
  | ChatSpacePrefillData
  | MemberSpacePrefillData
  | ImageSpacePrefillData;

interface FormDefaults {
  event_auto_rsvp_enabled?: boolean;
  name?: string;
  notification_type?: string[] | string;
  space_group_id?: number;
  visibility?: string;
}

type LLMSpaceType =
  | "posts"
  | "events"
  | "chat"
  | "course"
  | "members"
  | "images";

type NotificationField =
  | "default_notification_setting"
  | "default_in_app_notification_setting"
  | "default_mobile_notification_setting";

const mapLLMSpaceTypeToSpaceType = (spaceType: LLMSpaceType): SpaceType => {
  const mapping: Record<LLMSpaceType, SpaceType> = {
    posts: "basic",
    events: "event",
    chat: "chat",
    course: "course",
    members: "members",
    images: "image",
  };
  return mapping[spaceType] || "basic";
};

const convertNotificationSettings = (
  data: Pick<PostSpacePrefillData, NotificationField>,
): string[] | undefined => {
  const notificationFields: NotificationField[] = [
    "default_notification_setting",
    "default_in_app_notification_setting",
    "default_mobile_notification_setting",
  ];

  const enabledNotifications = notificationFields.filter(
    field => data[field] === true,
  );

  return enabledNotifications.length > 0 ? enabledNotifications : undefined;
};

const convertNotificationSpacePrefill = (
  data: PostSpacePrefillData | ImageSpacePrefillData | EventSpacePrefillData,
): Partial<FormDefaults> => {
  const defaults: Partial<FormDefaults> = {};
  const notificationTypes = convertNotificationSettings(data);

  if (notificationTypes) {
    defaults.notification_type = notificationTypes;
  }

  // Handle event-specific auto RSVP field
  if (
    "auto_rsvp_invited_attendees" in data &&
    typeof data.auto_rsvp_invited_attendees === "boolean"
  ) {
    defaults.event_auto_rsvp_enabled = data.auto_rsvp_invited_attendees;
  }

  return defaults;
};

const convertChatSpacePrefill = (
  data: ChatSpacePrefillData,
): Partial<FormDefaults> => {
  const defaults: Partial<FormDefaults> = {};

  if (data.notification_type) {
    defaults.notification_type = data.notification_type;
  }

  return defaults;
};

export const convertPrefillDataToFormDefaults = (
  prefillData: SpacePrefillData,
  spaceType: string,
): Partial<FormDefaults> => {
  const defaults: Partial<FormDefaults> = {};

  if (prefillData.name) {
    defaults.name = prefillData.name;
  }

  if (prefillData.access) {
    defaults.visibility = prefillData.access;
  }

  if (prefillData.space_group_id) {
    defaults.space_group_id = prefillData.space_group_id;
  }

  switch (spaceType) {
    case "basic":
    case "post":
    case "image":
    case "event":
      if (
        "default_notification_setting" in prefillData ||
        "auto_rsvp_invited_attendees" in prefillData
      ) {
        Object.assign(defaults, convertNotificationSpacePrefill(prefillData));
      }
      break;
    case "chat":
      if ("notification_type" in prefillData) {
        Object.assign(defaults, convertChatSpacePrefill(prefillData));
      }
      break;
    case "course":
    case "members":
      // These types only support name and access (already handled above)
      break;
  }

  return defaults;
};

export const mapDeepLinkSpaceType = (
  prefillData: SpacePrefillData,
): SpaceType =>
  prefillData.space_type
    ? mapLLMSpaceTypeToSpaceType(prefillData.space_type)
    : "basic";
